import { Box, Button, Link, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import { Link as RouterLink, useNavigate } from '@tanstack/react-router';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Scrollbar } from '../../../../components/atoms/scrollbar';
import { Status } from '../../../../components/atoms/status';
import AvatarWithInfo from '../../../../components/molecules/avatar-with-info';
import { DestinationGroup } from '../../api/types/destination-group';

import TableActionCell from '@/components/atoms/table-action-cell';
import { DataStatusIndicator } from '@/components/molecules/data-status-indicator/data-status-indicator';

export interface DestinationGroupsActionSlotPayload {
  id: number;
  isDefaultForOrganization: boolean;
}

interface Props {
  data: DestinationGroup[];
  error: string;
  isLoading: boolean;
  actionSlot: (payload: DestinationGroupsActionSlotPayload) => ReactNode;
}

export const DestinationGroupsTable = ({ data, error, isLoading, actionSlot }: Props) => {
  const { t } = useTranslation(['destinationGroups', 'common']);

  const navigate = useNavigate({ from: '/destination-groups/' });

  const columns = [
    {
      id: 'name',
      label: t('destinationGroups:table.name'),
    },
    {
      id: 'isDefault',
      label: t('destinationGroups:table.isDefault'),
    },
  ];

  return (
    <Box
      sx={{
        display: 'flex',
        flex: 1,
        flexDirection: 'column',
      }}>
      <Scrollbar>
        <Table sx={{ minWidth: 1000 }}>
          <TableHead>
            <TableRow>
              {columns.map(column => (
                <TableCell key={column.id}>{column.label}</TableCell>
              ))}
              <TableCell />
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {data?.map(group => (
              <TableRow hover key={group.id}>
                <TableCell>
                  <Link
                    color="primary"
                    underline="none"
                    component={RouterLink}
                    to={`${group.id}`}
                    variant="inherit">
                    <AvatarWithInfo
                      avatarText={`${group.name.slice(0, 1)}`}
                      primaryText={`${group.name} `}
                      secondaryText=""
                      src=""
                    />
                  </Link>
                </TableCell>
                <TableCell>
                  <Status
                    color={group.isDefaultForOrganization ? '#52bb8b' : '#ef6f6b'}
                    label={group.isDefaultForOrganization ? t('common:yes') : t('common:no')}
                  />
                </TableCell>
                <TableCell align="right">
                  <Button
                    variant="outlined"
                    onClick={() => {
                      navigate({ to: `${group.id}` });
                    }}>
                    {t('common:seeDetails')}
                  </Button>
                </TableCell>
                <TableActionCell>
                  {actionSlot({
                    id: group.id,
                    isDefaultForOrganization: group.isDefaultForOrganization,
                  })}
                </TableActionCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Scrollbar>
      <DataStatusIndicator dataLength={data?.length} error={error} isFetching={isLoading} />
    </Box>
  );
};
